import {
  isLoadingProjectSearchListSelector,
  poiSearchProjectListSelector,
  projectsByIdsSelector,
} from 'store/state/domainData/selectors';
import {
  makeMapStateToPropsFactory,
  PoiGetter,
} from 'components/cards/listing/makeMapStateToPropsFactory';
import { State } from 'store/state';
import { IBulletin, IProject, MarketplaceType, PoiId, ResolutionPreference } from 'utils/entities';
import { UniversalCardProps } from 'components/cards/listing/UniversalCard/UniversalCard';
import UniversalCard from 'components/cards/listing/UniversalCard';
import { connect } from 'react-redux';
import React from 'react';
import Carousel from 'ds/components/carousels/carousel';
import { ItemWrapper } from 'ds/components/carousels/carousel/styled';
import { Link } from 'components/link';
import { getLinkRouteParams } from 'ds/components/carousels/carousel/utils';
import { ShowAt } from 'consts/breakpoints';
import { marketplaceSelector, routeSelector } from 'store/state/selectors/router';
import { Route, State as RouteState } from 'config/routes';
import { ScrollBox } from 'ds/components/carousels/scroll-box';
import { CenteredCarouselControls } from 'ds/components/carousels/styled';
import { routeByPoiTypeAndMarketplace } from 'utils/marketplaceRoutes';
import { CardType } from 'components/cards/types';

interface ProjectCarouselProps {
  projects: Array<IBulletin | IProject>;
  isLoadingProjects: boolean;
  route: RouteState;
  marketplace: MarketplaceType;
}

const poiByIdsSelector: PoiGetter = (state: State, poiId: PoiId) => projectsByIdsSelector(state)[poiId];

const makeMapStateToProps = makeMapStateToPropsFactory(poiByIdsSelector);

const SpecificUniversalCard = (props: UniversalCardProps) => {
  let enhancedProps: UniversalCardProps = {
    ...props,
    hideLogoPreview: true,
    isMinHeight: true,
  };
  if (enhancedProps.cardType === CardType.Bulletin || enhancedProps.cardType === CardType.CommercialBulletin) {
    enhancedProps = {
      ...enhancedProps,
      resolutionPreferences: ResolutionPreference.Neighborhood,
    };
  }
  return <UniversalCard {...enhancedProps} />;
};

const ConnectedUniversalCard = connect(makeMapStateToProps)(SpecificUniversalCard);

const KEY = Symbol('projectsListCarousel');

function getSource(routeName: Route) {
  if (routeName === Route.UnitPage) return 'new_projects_nearby_carousel';
  else if (routeName === Route.ProjectPage) return 'new_projects_nearby_carousel_non_promoted';
  else if (routeName === Route.Sold) return 'sold_page';
  return null;
}

export const ProjectCarousel: React.FC<ProjectCarouselProps> = ({ projects, isLoadingProjects, route, marketplace }) => {
  if (!projects) return null;

  const hasArrows = projects.length > 2;

  const projectsList = projects.map(({ id, type }) => (
    <ItemWrapper key={id}>
      <Link
        routeName={routeByPoiTypeAndMarketplace(type, marketplace)}
        routeParams={getLinkRouteParams(id, route.params, getSource(route.name))}
        hideLinkParams
      >
        <ConnectedUniversalCard id={id} />
      </Link>
    </ItemWrapper>
  ));

  return (
    <>
      <ShowAt to={2}>
        <ScrollBox cacheKey={KEY} autoWidth>
          {projectsList}
        </ScrollBox>
      </ShowAt>
      <ShowAt from={3}>
        <CenteredCarouselControls>
          <Carousel
            isLoading={isLoadingProjects}
            hasArrows={hasArrows}
          >
            {projectsList}
          </Carousel>
        </CenteredCarouselControls>
      </ShowAt>
    </>
  );
};

const mapStateToProps = (state: State) => ({
  projects: poiSearchProjectListSelector(state),
  isLoadingProjects: isLoadingProjectSearchListSelector(state),
  route: routeSelector(state),
  marketplace: marketplaceSelector(state),
});

export default connect(mapStateToProps, null)(ProjectCarousel);
